import type { SessionInfo, Club, Team, NotificationPreference, TeamUser, VerifyCodeRequest } from '../types';
import { locale } from 'svelte-i18n';
import { get } from 'svelte/store';
import { settingsStore } from '../stores/settings';

// Helper function to get current locale
function getCurrentLocale(): string {
  // First try to get from settingsStore (primary source)
  const settings = get(settingsStore);
  if (settings && settings.language) {
    return settings.language;
  }
  
  // Fallback to localStorage
  const storedLocale = localStorage.getItem('preferred-language');
  if (storedLocale) {
    return storedLocale;
  }
  
  // Then try to get from svelte-i18n store
  const currentLocale = get(locale);
  if (currentLocale) {
    // Store it in localStorage for future use
    localStorage.setItem('preferred-language', currentLocale);
    return currentLocale;
  }
  
  // Default to Dutch if nothing is found
  const defaultLocale = 'nl';
  localStorage.setItem('preferred-language', defaultLocale);
  return defaultLocale;
}

// Helper function to get current token dynamically
function getCurrentToken(): string {
  return localStorage.getItem('token') || '';
}

// Base API request with language header (without Authorization - will be added dynamically)
const api_request = {
  method: 'GET',
  mode: 'same-origin',
  cache: 'no-cache',
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': getCurrentLocale(),
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  redirect: 'follow',
  referrerPolicy: 'origin',
};

const get_request = { 
  ...api_request, 
  method: 'GET',
};

const post_request = { 
  ...api_request, 
  method: 'POST' 
};

const put_request = { 
  ...api_request, 
  method: 'PUT' 
};

const delete_request = { 
  ...api_request, 
  method: 'DELETE' 
};

const formdata_request = {
  ...api_request,
  method: 'POST',
  headers: {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Accept-Language': getCurrentLocale()
  },
};

// Constants and helper functions
// @ts-ignore - vite-specific env variable
const API_URL = import.meta.env.VITE_API_URL;

function replacer(key: string, value: any) {
  if (value === undefined) {
    return null;
  }
  return value;
}

// Main caller function
async function doAPICall(
  url: string, 
  data: any, 
  callback: (result: any, status: number) => any,
  customHeaders?: Record<string, string>
) {
  try {
    // Check if we need to update the language header with latest locale
    const currentLocale = getCurrentLocale();
    let headers = { ...data.headers };
    
    // Only update Accept-Language if it's different from what's in headers
    if (headers['Accept-Language'] !== currentLocale) {
      headers['Accept-Language'] = currentLocale;
    }
    
    // Add Authorization header dynamically (unless it's already present)
    if (!headers['Authorization']) {
      const currentToken = getCurrentToken();
      if (currentToken) {
        headers['Authorization'] = `Bearer ${currentToken}`;
      }
    }
    
    // Add any custom headers
    if (customHeaders) {
      headers = { ...headers, ...customHeaders };
    }

    // Add cache-busting parameter for GET requests
    let finalUrl = url;
    if (data.method === 'GET') {
      const separator = url.includes('?') ? '&' : '?';
      finalUrl = `${url}${separator}_t=${Date.now()}`;
    }

    const requestData = {
      ...data,
      headers
    };

    const response = await fetch(finalUrl, requestData);
    const result = await response.json();

    if (!response.ok) {
      console.log('response is not ok', result);
      if (result.detail) {
        result.errors = [result.detail];
      }
      if (result.errors) {
        let errorMessage = result.errors.join(' ');
        return callback(errorMessage, response.status);
      }
      return callback('Something went wrong, please try again', response.status);
    }
    return callback(result, response.status);
  } catch (error) {
    console.error('API call error:', error);
    return callback('Something went wrong, please try again', 400);
  }
}

// Auth-related API calls
export async function requestMagicLink(phoneNumber: string, countryCode: string, returnTo: string, callback: (result: { token: string }, status: number) => void) {
  const data = { 
    ...post_request, 
    body: JSON.stringify({ phone_number: phoneNumber, country_code: countryCode, return_to: returnTo }) 
  };
  return doAPICall(`${API_URL}/auth/magic-link`, data, callback);
}

export async function verifyMagicLink(token: string, callback: (result: { access_token: string, resend?: boolean }, status: number) => void) {
  const data = { 
    ...post_request, 
    body: JSON.stringify({ token }) 
  };
  return doAPICall(`${API_URL}/auth/verify`, data, callback);
}

export async function verifyCode(payload: VerifyCodeRequest, callback: (result: { access_token: string, resend?: boolean }, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify(payload)
  };
  return doAPICall(`${API_URL}/auth/verify-signup`, data, callback);
}

export async function resendMagicLink(token: string, callback: (result: any, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify({ token })
  };
  return doAPICall(`${API_URL}/auth/resend-magic-link`, data, callback);
}

export async function getCurrentUser(callback: (result: any, status: number) => void) {
  const data = { 
    ...get_request
    // Authorization header will be added dynamically in doAPICall
  };
  return doAPICall(`${API_URL}/auth/me`, data, callback);
}
// API Functions

function getRequestWithAuth(baseRequest: typeof api_request) {
  return {
    ...baseRequest
    // Authorization header will be added dynamically in doAPICall
    // Don't add Accept-Language here since it's already in baseRequest.headers
  };
}

export async function getSessionInfo(callback: (result: SessionInfo, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  const url = `${API_URL}/session/`;
  return doAPICall(url, data, callback);
}

export async function updateUserdata(userData: object, callback: (result: any, status: number) => void) {
  const data = { 
    ...put_request,
    body: JSON.stringify(userData)
    // Authorization header will be added dynamically in doAPICall
  };
  return doAPICall(`${API_URL}/auth/userdata`, data, callback);
}

export async function updateTeam(teamData: Team, callback: (result: Team | string, status: number) => void) {
  const data = { ...post_request, body: JSON.stringify(teamData, replacer) };
  return doAPICall(`${API_URL}/session/team`, data, callback);
}

export async function createTeam(teamData: { club_id: string, team_name: string }, callback: (result: Team | string, status: number) => void) {
  const data = { 
    ...getRequestWithAuth(post_request), 
    body: JSON.stringify(teamData, replacer) 
  };
  return doAPICall(`${API_URL}/session/create-team`, data, callback);
}

export async function deleteTeam(teamId: string, callback: (result: Team | string, status: number) => void) {
  const data = { 
    ...getRequestWithAuth(delete_request), 
    body: JSON.stringify({ team_id: teamId }, replacer) 
  };
  return doAPICall(`${API_URL}/session/delete-team`, data, callback);
}

// Image upload
export async function uploadImage(file: File, callback: (result: { url: string }, status: number) => void) {
  const formData = new FormData();
  formData.append('file', file);
  const data = {
    ...getRequestWithAuth(api_request),
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Accept-Language': getCurrentLocale(),
      'X-Requested-With': 'XMLHttpRequest'
      // Authorization header will be added dynamically in doAPICall
    },
    body: formData
  };
  return doAPICall(`${API_URL}/upload-image/`, data, callback);
}

export async function getClubs(type: string, callback: (result: Club[], status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/session/clubs?filter=${type}`, data, callback);
}

// Public clubs endpoint (no authentication required)
export async function getPublicClubs(sportType: string, callback: (result: Club[], status: number) => void) {
  const data = { 
    ...get_request,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': getCurrentLocale()
    }
  };
  return doAPICall(`${API_URL}/session/public/clubs/${sportType}`, data, callback);
}

// User signup
export async function signup(signupData: any, callback: (result: any, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify(signupData)
  };
  return doAPICall(`${API_URL}/account/signup`, data, callback);
}

// Signup step one (send verification code)
export async function signupStepOne(signupData: { phone_number: string, country_code: string, messaging_app: string, invitation_code: string, resend?: boolean, access_grant_token?: string }, callback: (result: any, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify(signupData)
  };
  return doAPICall(`${API_URL}/account/signup-step-one`, data, callback);
}

// Signup step two
export async function signupStepTwo(signupData: { sports_type: string, club_id: string, team_name: string, username: string, access_grant_token?: string }, callback: (result: any, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify(signupData)
  };
  return doAPICall(`${API_URL}/account/signup-step-two`, data, callback);
}

// Resend onboarding messages
export async function resendOnboardingMessages(phoneNumber: string, countryCode: string, callback: (result: any, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify({ phone_number: phoneNumber, country_code: countryCode })
  };
  return doAPICall(`${API_URL}/account/resend-onboarding`, data, callback);
}

// Language settings
export async function updateLanguage(language: string, callback: (result: any, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify({ language })
  };
  return doAPICall(`${API_URL}/session/language`, data, callback);
}

// Get available languages
export async function getLanguages(callback: (result: any, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/session/languages`, data, callback);
}

// Switch active team
export async function switchTeam(teamId: string, callback: (result: any, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ team_id: teamId }, replacer)
  };
  return doAPICall(`${API_URL}/account/switch-team`, data, callback);
}

// Delete account
export async function deleteAccount(callback: (result: any, status: number) => void) {
  const data = getRequestWithAuth(delete_request);
  return doAPICall(`${API_URL}/account/delete`, data, callback);
}

// Check if user has seen onboarding popup (auto-marks as seen if not)
export async function checkOnboardingPopup(onboardingStep: string, callback: (result: { onboarding_step: string, has_seen: boolean, date_seen: string, newly_seen: boolean }, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/account/onboarding-popup/${encodeURIComponent(onboardingStep)}`, data, callback);
}

// Notification preferences
export async function getNotificationPreferences(callback: (result: { preferences: NotificationPreference[] }, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/auth/notification-preferences`, data, callback);
}

export async function updateNotificationPreference(
  notificationType: 'match_announcement' | 'match_start' | 'match_report_reminder',
  enabled: boolean,
  callback: (result: { success: boolean, preference: NotificationPreference }, status: number) => void
) {
  const data = {
    ...getRequestWithAuth(put_request),
    body: JSON.stringify({ 
      notification_type: notificationType, 
      enabled: enabled 
    })
  };
  return doAPICall(`${API_URL}/auth/notification-preferences`, data, callback);
}

// Team Users API functions
export async function getTeamUsers(teamId: string, callback: (result: { team_users: TeamUser[], total_count: number }, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/session/team/${encodeURIComponent(teamId)}/users`, data, callback);
}

export async function inviteTeamUser(teamId: string, callback: (result: { invitation_link: string, message: string }, status: number) => void) {
  const data = getRequestWithAuth(post_request);
  return doAPICall(`${API_URL}/session/team/${encodeURIComponent(teamId)}/users/invite`, data, callback);
}

export async function removeTeamUser(teamId: string, userId: string, callback: (result: { message: string }, status: number) => void) {
  const data = getRequestWithAuth(delete_request);
  return doAPICall(`${API_URL}/session/team/${encodeURIComponent(teamId)}/users/${encodeURIComponent(userId)}`, data, callback);
}

// Access Grant API functions
export async function verifyAccessGrant(token: string, callback: (result: { is_valid: boolean, actions: any, expires: string, created_by_user_id: string }, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify({ token })
  };
  return doAPICall(`${API_URL}/auth/verify-grant`, data, callback);
}

// Join team via invitation (for logged-in users)
export async function joinTeamViaInvitation(accessGrantToken: string, callback: (result: { message: string, team_id: string, team_name: string }, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ access_grant_token: accessGrantToken })
  };
  return doAPICall(`${API_URL}/session/team/join`, data, callback);
}

// Send magic link for login
export async function sendMagicLink(phoneNumber: string, countryCode: string, returnTo: string, callback: (result: { result: string, messaging_app: string, message: string }, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify({
      phone_number: phoneNumber,
      country_code: countryCode,
      return_to: returnTo
    })
  };
  return doAPICall(`${API_URL}/auth/magic-link`, data, callback);
}

// Email authentication functions

// Send email magic link for login
export async function sendEmailMagicLink(email: string, returnTo: string, callback: (result: { result: string, message: string }, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify({
      email: email,
      return_to: returnTo
    })
  };
  return doAPICall(`${API_URL}/auth/email-magic-link`, data, callback);
}

// Signup step one with email (send verification code)
export async function signupStepOneEmail(signupData: { email: string, invitation_code: string, resend?: boolean, access_grant_token?: string }, callback: (result: any, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify(signupData)
  };
  return doAPICall(`${API_URL}/account/signup-step-one-email`, data, callback);
}

// Verify email code for signup
export async function verifyEmailSignup(payload: { code: number, email: string, invitation_code?: string }, callback: (result: { access_token: string, return_to?: string }, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify(payload)
  };
  return doAPICall(`${API_URL}/auth/verify-email-signup`, data, callback);
}

// Initiate email change (send verification code to new email)
export async function initiateEmailChange(newEmail: string, callback: (result: { result: string, message: string }, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ new_email: newEmail })
  };
  return doAPICall(`${API_URL}/auth/initiate-email-change`, data, callback);
}

// Confirm email change (verify code and update email)
export async function confirmEmailChange(code: number, callback: (result: { result: string, message: string, email: string }, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ code: code })
  };
  return doAPICall(`${API_URL}/auth/confirm-email-change`, data, callback);
}

// Analytics function - uses Umami client-side tracking
export async function trackEvent(action: string, eventData: Record<string, any> = {}, userId?: string): Promise<boolean> {
  try {
    // Check if umami is available (script loaded, only in production)
    if (typeof umami === 'undefined') {
      return false;
    }

    // Optionally identify the user for session tracking
    if (userId) {
      umami.identify(userId);
    }

    // Track the event with data
    umami.track(action, eventData);
    return true;

  } catch (error) {
    console.warn('Analytics tracking error:', error);
    return false;
  }
}

// Club Management API functions (Superuser only)
export async function getAllClubs(callback: (result: Club[], status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/clubs/all`, data, callback);
}

export async function getClubLogos(callback: (result: { filename: string, url: string, key_path: string }[], status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/clubs/logos`, data, callback);
}

export async function createClub(clubData: Partial<Club>, callback: (result: Club, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify(clubData, replacer)
  };
  return doAPICall(`${API_URL}/clubs`, data, callback);
}

export async function updateClub(clubId: string, clubData: Partial<Club>, callback: (result: Club, status: number) => void) {
  const data = {
    ...getRequestWithAuth(put_request),
    body: JSON.stringify(clubData, replacer)
  };
  return doAPICall(`${API_URL}/clubs/${encodeURIComponent(clubId)}`, data, callback);
}

export async function deleteClub(clubId: string, callback: (result: { success: boolean, message: string }, status: number) => void) {
  const data = getRequestWithAuth(delete_request);
  return doAPICall(`${API_URL}/clubs/${encodeURIComponent(clubId)}`, data, callback);
}

export async function uploadClubLogo(file: File, callback: (result: { filename: string, url: string, key_path: string, message: string }, status: number) => void) {
  const formData = new FormData();
  formData.append('file', file);
  const data = {
    ...getRequestWithAuth(api_request),
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Accept-Language': getCurrentLocale(),
      'X-Requested-With': 'XMLHttpRequest'
      // Authorization header will be added dynamically in doAPICall
    },
    body: formData
  };
  return doAPICall(`${API_URL}/clubs/upload-logo`, data, callback);
}

